// The Building readiness record's citations, resolved once for the whole tab or card
// (with ``extra``, any other record the tab shows, such as the structural history):
// every source field and every citation written in its text links to the file it
// names, and opens it at the quoted words (the same viewer every other page uses).
import { useMemo, type ReactNode } from "react";

import type { BuildingReadiness } from "../api/hooks/buildingReadiness";
import { Markdown } from "./Markdown";
import { useCitations } from "./useCitations";

// A source field as the citation text the shared viewer reads, built as the backend's
// readiness_cited_text builds it for the gate: whitespace collapsed (a citation is one
// line) and brackets made parentheses (a bracket would end it early).
export const sourceCitation = (
  uploadId: string,
  quote: string | undefined,
): string => {
  const words = (quote ?? "")
    .split(/\s+/)
    .join(" ")
    .replace(/\[/g, "(")
    .replace(/\]/g, ")")
    .trim();
  return words ? `[${uploadId} "${words}"]` : `[${uploadId}]`;
};

const bodies = (value: unknown, out: string[]): string[] => {
  if (typeof value === "string") {
    if (value.includes("[")) out.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => bodies(item, out));
  } else if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.upload_id === "string" && record.upload_id) {
      out.push(
        sourceCitation(
          record.upload_id,
          typeof record.quote === "string" ? record.quote : "",
        ),
      );
    }
    Object.values(record).forEach((item) => bodies(item, out));
  }
  return out;
};

export const useReadinessCitations = (
  readiness: BuildingReadiness | null | undefined,
  extra: unknown = null,
): { cite: (text: string) => ReactNode; modal: ReactNode } =>
  useCitedTexts(
    useMemo(() => bodies([readiness ?? {}, extra], []), [readiness, extra]),
  );

/** ``texts``' citations resolved once, and a ``cite`` that renders any of them as the
 * file's name opening at the quote; text with no citation stays plain. */
export const useCitedTexts = (
  texts: string[],
): { cite: (text: string) => ReactNode; modal: ReactNode } => {
  const citations = useCitations(texts);
  // Text with no citation stays plain text, as it was before citations rendered.
  const cite = (text: string): ReactNode => {
    if (!text) return null;
    if (!text.includes("[")) return text;
    return (
      <Markdown inline components={citations.components}>
        {citations.transform(text)}
      </Markdown>
    );
  };
  return { cite, modal: citations.modal };
};
